import type { Attention } from "../../lib/sales-types";
import { cn } from "../ui/cn";

/**
 * The "this is being neglected" flag.
 *
 * Never colour alone. The brief asks for that explicitly and it is right: a
 * red pill and a grey pill are the same pill to eight percent of men, and this
 * is a sales floor where the whole point of the screen is that the flagged rows
 * jump out. So every state carries a glyph *and* a word as well as a tint, and
 * the tooltip says how far past the rule it is.
 *
 * The exclamation is drawn rather than typed. A `⚠` renders as an emoji on
 * macOS and as a hollow outline on Windows, at different sizes, and a badge
 * whose height changes per platform is the sort of thing that quietly breaks a
 * table's row rhythm.
 */
export function AttentionBadge({
  attention,
  size = "sm",
  reasonAlreadySaid,
}: {
  attention: Attention;
  size?: "sm" | "xs";
  /**
   * The surrounding section already names the reason, so do not repeat it.
   *
   * Set by the board's needs-attention panel, where the rows sit under a
   * heading that reads "Going cold" and every badge beneath it then read
   * "Going cold" as well. Three things on that row were saying one thing --
   * the heading, the red edge and the pill -- and the pill was the one costing
   * the company name its width.
   *
   * It silences only the overdue pill. "Waiting" is not what any of those
   * headings say, and it is the one state that means *do not chase this*, so
   * losing it to a tidy-up would be losing the opposite of what the heading
   * claims.
   */
  reasonAlreadySaid?: boolean;
}) {
  if (attention.waiting) {
    return (
      <span
        title="They asked to be rung back later, so this is not being chased yet"
        className={cn(
          "inline-flex shrink-0 items-center gap-1 rounded-full font-medium",
          "bg-surface-2 text-muted-on-tint",
          size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs",
        )}
      >
        <ClockGlyph />
        Waiting
      </span>
    );
  }

  if (!attention.overdue || reasonAlreadySaid) return null;

  const label =
    attention.reason === "never_attempted"
      ? "Never tried"
      : attention.reason === "past_due"
        ? "Past due"
        : "Going cold";

  const why =
    attention.reason === "never_attempted"
      ? `Raised ${describeOver(attention.hoursOver)} past the no-attempt rule and nobody has rung yet`
      : attention.reason === "past_due"
        ? `${describeOver(attention.hoursOver)} past its due date`
        : `${describeOver(attention.hoursOver)} past the follow-up rule since the last attempt`;

  return (
    <span
      title={why}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full font-medium",
        /* A tinted background rather than a solid red block: there can be
           twenty of these on screen at once, and twenty solid reds is a wall
           nobody reads. */
        "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300",
        size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs",
      )}
    >
      <BangGlyph />
      {label}
    </span>
  );
}

/** Hours, or days once hours stop being a number anybody reads. */
function describeOver(hours: number): string {
  if (hours < 1) return "less than an hour";
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;
  return `${Math.floor(hours / 24)} days`;
}

function BangGlyph() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M12 5v9" />
      <path d="M12 19h.01" />
    </svg>
  );
}

function ClockGlyph() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
